import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { settings as settingsApi } from '@/api/endpoints'
import { Alert, Input, Modal, Spinner } from '@/components/ui'
import { currencySymbol } from '@/lib/utils'
import type { MissingTariff } from '@/types/api'

interface Props {
  tariff: MissingTariff
  /** Uložené lekcie, ktoré sa majú hneď preceniť (aj keď sú v minulosti). */
  clientReservationIds?: number[]
  onClose: () => void
}

/**
 * „Doplniť cenu do cenníka“ - z okna platby, keď pre lekciu cenník nemá
 * sadzbu. Variant (osoby, dĺžka, jazyk) je daný lekciou, správca doplní len
 * sumu. Uloží sa do cenníka ako v Nastaveniach (`POST /pricing/row`) a lekcie
 * sa precenia.
 */
export default function MissingPriceDialog({ tariff, clientReservationIds, onClose }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [price, setPrice] = useState('')
  const [error, setError] = useState<string | null>(null)
  const parsed = Number.parseFloat(price.replace(',', '.'))
  const valid = price.trim() !== '' && !Number.isNaN(parsed) && parsed >= 0

  const save = useMutation({
    mutationFn: () =>
      settingsApi.savePricingRow({
        ...tariff,
        price: parsed.toFixed(2),
        client_reservation_ids: clientReservationIds,
      }),
    onSuccess: () => {
      for (const key of ['pricing', 'pricing-missing', 'reservation-quote', 'order', 'fees', 'calendar']) {
        void qc.invalidateQueries({ queryKey: [key] })
      }
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t('payments.addTariffTitle')}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="submit"
            form="missing-price"
            className="btn-primary"
            disabled={!valid || save.isPending}
          >
            {save.isPending ? <Spinner /> : null}
            {t('payments.addTariffSave')}
          </button>
        </>
      }
    >
      <form
        id="missing-price"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) save.mutate()
        }}
      >
        {error ? <Alert variant="error">{error}</Alert> : null}
        <p className="text-sm text-slate-600">{t('payments.addTariffText')}</p>
        <dl className="grid grid-cols-3 gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <div>
            <dt className="text-xs uppercase text-slate-500">{t('reservation.persons')}</dt>
            <dd className="font-medium">{tariff.person}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-slate-500">{t('settings.pricingLength')}</dt>
            <dd className="font-medium">{t('rewards.minutes', { count: tariff.minutes })}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-slate-500">{t('reservation.language')}</dt>
            <dd className="font-medium">
              {tariff.lang ? tariff.lang.toUpperCase() : t('payments.foreignTariff')}
            </dd>
          </div>
        </dl>
        <label className="block">
          <span className="label">{t('reservation.price')}</span>
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              type="number"
              min={0}
              step="0.01"
              className="w-32 text-right"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <span className="text-slate-600">{currencySymbol()}</span>
          </div>
        </label>
        <p className="text-xs text-slate-500">{t('payments.addTariffHint')}</p>
      </form>
    </Modal>
  )
}
